import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, AlertCircle, Volume2, VolumeX, Users, Sun, Moon, Eye, EyeOff, Maximize, Minimize, Plus, Minus, CircleStop } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

import { LANGUAGES, flagClass } from '../lib/languagesCatalog';
import 'flag-icons/css/flag-icons.min.css';

// Bloco C: sound test button labels (per UI language)
const SOUND_TEST_LABELS = {
  pt: { test: 'Testar som', playing: 'A reproduzir...', ok: 'Som OK — testar de novo', err: 'Nao foi possivel reproduzir o teste' },
  en: { test: 'Test sound', playing: 'Playing...', ok: 'Sound OK — test again', err: 'Could not play the sound test' },
  es: { test: 'Probar sonido', playing: 'Reproduciendo...', ok: 'Sonido OK — probar de nuevo', err: 'No se pudo reproducir la prueba' },
  fr: { test: 'Tester le son', playing: 'Lecture...', ok: 'Son OK — tester encore', err: 'Impossible de lire le test' },
  de: { test: 'Ton testen', playing: 'Wiedergabe...', ok: 'Ton OK — erneut testen', err: 'Test konnte nicht abgespielt werden' },
};

// UI translations for bilingual interface
const UI_STRINGS = {
  pt: {
    liveTranslation: 'Traducao Ao Vivo',
    session: 'Sessao',
    chooseLang: 'Escolher idioma',
    interpreterMode: 'Modo Interprete',
    modeActive: 'Modo Ativo',
    soundOn: 'Som ON',
    soundOff: 'Som OFF',
    connected: 'Conectado',
    disconnected: 'Desconectado',
    waitingSubtitles: 'Aguardando legendas...',
    selectLangAbove: 'Por favor, selecione um idioma acima',
    original: 'Original',
    translation: 'Traducao',
    latency: 'Latencia',
    leaveSession: 'Sair da Sessao',
    loading: 'A carregar sessao...',
    error: 'Erro',
    sessionNotFound: 'Sessao nao encontrada',
    backToHome: 'Voltar ao Inicio',
    showOriginal: 'Mostrar original',
    hideOriginal: 'Ocultar original',
    teleprompter: 'Teleprompter',
    normalMode: 'Normal',
    fontSize: 'Tamanho',
    theme: 'Tema',
    changeLang: 'Mudar idioma',
    welcomeTitle: 'Bem-vindo a sessao',
    welcomeText: 'Selecione o idioma para receber a traducao em tempo real.',
    speakerActive: 'A traduzir...',
    sessionActive: 'Sessao ativa',
    readyToReceive: 'Pronto para receber traducao',
    connectTranslation: 'Conectar traducao',
    connectingTranslation: 'A conectar...',
    verifiedConnected: 'Ligacao verificada',
  },
  en: {
    liveTranslation: 'Live Translation',
    session: 'Session',
    chooseLang: 'Choose language',
    interpreterMode: 'Interpreter Mode',
    modeActive: 'Mode Active',
    soundOn: 'Sound ON',
    soundOff: 'Sound OFF',
    connected: 'Connected',
    disconnected: 'Disconnected',
    waitingSubtitles: 'Waiting for subtitles...',
    selectLangAbove: 'Please select a language above',
    original: 'Original',
    translation: 'Translation',
    latency: 'Latency',
    leaveSession: 'Leave Session',
    loading: 'Loading session...',
    error: 'Error',
    sessionNotFound: 'Session not found',
    backToHome: 'Back to Home',
    showOriginal: 'Show original',
    hideOriginal: 'Hide original',
    teleprompter: 'Teleprompter',
    normalMode: 'Normal',
    fontSize: 'Font size',
    theme: 'Theme',
    changeLang: 'Change language',
    welcomeTitle: 'Welcome to the session',
    welcomeText: 'Select a language to receive real-time translation.',
    speakerActive: 'Translating...',
    sessionActive: 'Session active',
    readyToReceive: 'Ready to receive translation',
    connectTranslation: 'Connect translation',
    connectingTranslation: 'Connecting...',
    verifiedConnected: 'Connection verified',
  },
  es: {
    liveTranslation: 'Traduccion en Vivo',
    session: 'Sesion',
    chooseLang: 'Elegir idioma',
    interpreterMode: 'Modo Interprete',
    modeActive: 'Modo Activo',
    soundOn: 'Sonido ON',
    soundOff: 'Sonido OFF',
    connected: 'Conectado',
    disconnected: 'Desconectado',
    waitingSubtitles: 'Esperando subtitulos...',
    selectLangAbove: 'Por favor, seleccione un idioma arriba',
    original: 'Original',
    translation: 'Traduccion',
    latency: 'Latencia',
    leaveSession: 'Salir de la Sesion',
    loading: 'Cargando sesion...',
    error: 'Error',
    sessionNotFound: 'Sesion no encontrada',
    backToHome: 'Volver al Inicio',
    showOriginal: 'Mostrar original',
    hideOriginal: 'Ocultar original',
    teleprompter: 'Teleprompter',
    normalMode: 'Normal',
    fontSize: 'Tamano',
    theme: 'Tema',
    changeLang: 'Cambiar idioma',
    welcomeTitle: 'Bienvenido a la sesion',
    welcomeText: 'Seleccione un idioma para recibir la traduccion en tiempo real.',
    speakerActive: 'Traduciendo...',
    sessionActive: 'Sesion activa',
    readyToReceive: 'Listo para recibir traduccion',
    connectTranslation: 'Conectar traduccion',
    connectingTranslation: 'Conectando...',
    verifiedConnected: 'Conexion verificada',
  },
  fr: {
    liveTranslation: 'Traduction en Direct',
    session: 'Session',
    chooseLang: 'Choisir la langue',
    interpreterMode: 'Mode Interprete',
    modeActive: 'Mode Actif',
    soundOn: 'Son ON',
    soundOff: 'Son OFF',
    connected: 'Connecte',
    disconnected: 'Deconnecte',
    waitingSubtitles: 'En attente de sous-titres...',
    selectLangAbove: 'Veuillez selectionner une langue ci-dessus',
    original: 'Original',
    translation: 'Traduction',
    latency: 'Latence',
    leaveSession: 'Quitter la Session',
    loading: 'Chargement de la session...',
    error: 'Erreur',
    sessionNotFound: 'Session introuvable',
    backToHome: 'Retour a l\'accueil',
    showOriginal: 'Afficher l\'original',
    hideOriginal: 'Masquer l\'original',
    teleprompter: 'Teleprompter',
    normalMode: 'Normal',
    fontSize: 'Taille',
    theme: 'Theme',
    changeLang: 'Changer de langue',
    welcomeTitle: 'Bienvenue dans la session',
    welcomeText: 'Selectionnez une langue pour recevoir la traduction en temps reel.',
    speakerActive: 'Traduction en cours...',
    sessionActive: 'Session active',
    readyToReceive: 'Pret a recevoir la traduction',
    connectTranslation: 'Connecter la traduction',
    connectingTranslation: 'Connexion en cours...',
    verifiedConnected: 'Connexion verifiee',
  },
  de: {
    liveTranslation: 'Live-Ubersetzung',
    session: 'Sitzung',
    chooseLang: 'Sprache wahlen',
    interpreterMode: 'Dolmetschermodus',
    modeActive: 'Modus Aktiv',
    soundOn: 'Ton AN',
    soundOff: 'Ton AUS',
    connected: 'Verbunden',
    disconnected: 'Getrennt',
    waitingSubtitles: 'Warte auf Untertitel...',
    selectLangAbove: 'Bitte wahlen Sie oben eine Sprache',
    original: 'Original',
    translation: 'Ubersetzung',
    latency: 'Latenz',
    leaveSession: 'Sitzung verlassen',
    loading: 'Sitzung wird geladen...',
    error: 'Fehler',
    sessionNotFound: 'Sitzung nicht gefunden',
    backToHome: 'Zuruck zur Startseite',
    showOriginal: 'Original anzeigen',
    hideOriginal: 'Original ausblenden',
    teleprompter: 'Teleprompter',
    normalMode: 'Normal',
    fontSize: 'Grosse',
    theme: 'Design',
    changeLang: 'Sprache andern',
    welcomeTitle: 'Willkommen zur Sitzung',
    welcomeText: 'Wahlen Sie eine Sprache, um die Echtzeit-Ubersetzung zu erhalten.',
    speakerActive: 'Ubersetzen...',
    sessionActive: 'Sitzung aktiv',
    readyToReceive: 'Bereit fur Ubersetzung',
    connectTranslation: 'Ubersetzung verbinden',
    connectingTranslation: 'Verbindung wird hergestellt...',
    verifiedConnected: 'Verbindung bestatigt',
  },
  it: {
    liveTranslation: 'Traduzione dal Vivo',
    session: 'Sessione',
    chooseLang: 'Scegli la lingua',
    interpreterMode: 'Modalita Interprete',
    modeActive: 'Modalita Attiva',
    soundOn: 'Audio ON',
    soundOff: 'Audio OFF',
    connected: 'Connesso',
    disconnected: 'Disconnesso',
    waitingSubtitles: 'In attesa di sottotitoli...',
    selectLangAbove: 'Seleziona una lingua sopra',
    original: 'Originale',
    translation: 'Traduzione',
    latency: 'Latenza',
    leaveSession: 'Esci dalla Sessione',
    loading: 'Caricamento sessione...',
    error: 'Errore',
    sessionNotFound: 'Sessione non trovata',
    backToHome: 'Torna alla Home',
    showOriginal: 'Mostra originale',
    hideOriginal: 'Nascondi originale',
    teleprompter: 'Teleprompter',
    normalMode: 'Normale',
    fontSize: 'Dimensione',
    theme: 'Tema',
    changeLang: 'Cambia lingua',
    welcomeTitle: 'Benvenuto nella sessione',
    welcomeText: 'Seleziona una lingua per ricevere la traduzione in tempo reale.',
    speakerActive: 'Traduzione in corso...',
    sessionActive: 'Sessione attiva',
    readyToReceive: 'Pronto a ricevere traduzione',
    connectTranslation: 'Connetti traduzione',
    connectingTranslation: 'Connessione in corso...',
    verifiedConnected: 'Connessione verificata',
  },
};

export default function ParticipantView() {
  const { code } = useParams();
  const navigate = useNavigate();

  // === AV Overlay Mode detection (query params) ===
  const searchParams = new URLSearchParams(window.location.search);
  const overlayMode = searchParams.get('mode') === 'overlay';
  const overlayBg = searchParams.get('bg') || 'green';
  const forcedLang = searchParams.get('lang') || null;
  const monitorMode = searchParams.get('monitor') === '1';
  const overlayBgColors = { green: '#00FF00', blue: '#0000FF', black: '#000000', transparent: 'transparent' };

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subtitles, setSubtitles] = useState([]);
  const [interimText, setInterimText] = useState(null);
  // Phase 1: utterance-scoped interim display state
  const interimStateRef = useRef({
    currentDisplayUtteranceId: null,
    hasTranslatedForCurrent: false,
    lastTranslatedRevisionForCurrent: 0,
  });
  const [connected, setConnected] = useState(false);
  const [selectedLang, setSelectedLang] = useState(null);
  const [currentSpeaker, setCurrentSpeaker] = useState('');
  const [userReady, setUserReady] = useState(overlayMode || monitorMode); // Overlay/monitor: auto-ready
  const [connecting, setConnecting] = useState(false); // WebSocket connecting + verify in progress
  const [interpreterMode, setInterpreterMode] = useState(false);
  const [secondLang, setSecondLang] = useState(null);
  const [subtitles2, setSubtitles2] = useState([]);
  const [interimText2, setInterimText2] = useState(null);
  // Phase 1: utterance-scoped interim display state (second WS)
  const interimState2Ref = useRef({
    currentDisplayUtteranceId: null,
    hasTranslatedForCurrent: false,
    lastTranslatedRevisionForCurrent: 0,
  });
  const [connected2, setConnected2] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [transmissionPaused, setTransmissionPaused] = useState(false);
  const transmissionPausedTimerRef = useRef(null);
  const wsRef = useRef(null);
  const wsRef2 = useRef(null);
  const scrollRef = useRef(null);
  const scrollRef2 = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  // iOS detection — audio OFF by default on iOS (autoplay blocked by Safari)
  const isIOSRef = useRef(/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
  const [audioEnabled, setAudioEnabled] = useState((overlayMode || monitorMode) ? false : !isIOSRef.current);
  const audioEnabledRef = useRef((overlayMode || monitorMode) ? false : !isIOSRef.current);
  const audioElementRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  // Bloco B: E2E ACKs + service status
  const ackEnabledRef = useRef(true);
  const [audioServiceSuspended, setAudioServiceSuspended] = useState(false);
  // Bloco D1: demo mode
  const [demoFull, setDemoFull] = useState(false);
  const demoFullRef = useRef(false);
  // Phase 4: Track currently playing chunk(s) for highlight/scroll
  const playingChunkIdsRef = useRef([]);
  const [playingChunkIds, setPlayingChunkIds] = useState([]);

  // New features state
  const [showOriginal, setShowOriginal] = useState(false);
  const [teleprompterMode, setTeleprompterMode] = useState(false);
  const [fontSize, setFontSize] = useState(1); // 0=small, 1=medium, 2=large, 3=xlarge
  const [darkMode, setDarkMode] = useState(true);
  const [lastActivity, setLastActivity] = useState(null);
  const [subtitleCount, setSubtitleCount] = useState(0);
  const [soundTestState, setSoundTestState] = useState('idle');
  const firstSubtitleRef = useRef(false);
  const wakeLockRef = useRef(null);
  // Session settings from host (remote toggles)
  const [sessionSettings, setSessionSettings] = useState({ teleprompter_enabled: true, interpreter_enabled: true });
  // Host speaking activity indicator
  const [hostSpeaking, setHostSpeaking] = useState(false);
  const hostSpeakingTimeoutRef = useRef(null);

  // Wake Lock - keep screen active on mobile
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch (e) {
        // Wake Lock not supported or denied
      }
    };
    requestWakeLock();
    // Re-acquire on visibility change (browser releases on tab switch)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
      }
    };
  }, []);

  // Derive UI language from selected translation language (fallback to 'pt')
  const uiLang = selectedLang && UI_STRINGS[selectedLang] ? selectedLang : 'pt';
  const t = UI_STRINGS[uiLang] || UI_STRINGS.pt;
  const stLabels = SOUND_TEST_LABELS[uiLang] || SOUND_TEST_LABELS.pt;

  // Bloco C: sound test with cached audio (also unlocks the audio context)
  const playSoundTest = async () => {
    if (!selectedLang || soundTestState === 'loading' || soundTestState === 'playing') return;
    setSoundTestState('loading');
    try {
      unlockAudio();
      const res = await axios.get(`${API}/sessions/by-code/${code}/sound-test/${selectedLang}`);
      const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
      audio.onended = () => setSoundTestState('played');
      audio.onerror = () => setSoundTestState('error');
      setSoundTestState('playing');
      await audio.play();
    } catch (e) {
      setSoundTestState('error');
      toast.error(stLabels.err);
    }
  };

  const FONT_SIZES = [
    { label: 'S', text: 'text-base', teleText: 'text-3xl' },
    { label: 'M', text: 'text-xl', teleText: 'text-5xl' },
    { label: 'L', text: 'text-2xl', teleText: 'text-6xl' },
    { label: 'XL', text: 'text-3xl', teleText: 'text-7xl' },
  ];

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await axios.get(`${API}/sessions/by-code/${code}`);
        const sess = response.data;
        setSession(sess);
        if (sess.status === 'stopped' || sess.status === 'archived') {
          setSessionEnded(true);
        }
        if (sess.settings) {
          setSessionSettings(sess.settings);
        }
        // Auto-select language if only 1 target lang (bilateral: all channels selectable)
        const availableLangs = (sess.target_langs || []).filter(l => sess.bilateral || l !== sess.source_lang);
        if (forcedLang && sess.target_langs.includes(forcedLang)) {
          setSelectedLang(forcedLang);
        } else if (availableLangs.length === 1) {
          setSelectedLang(availableLangs[0]);
        } else if ((overlayMode || monitorMode) && availableLangs.length > 0) {
          // Overlay mode: pick first available if no forced lang
          setSelectedLang(availableLangs[0]);
        }
        setLoading(false);
      } catch (err) {
        setError('Session not found');
        setLoading(false);
      }
    };
    fetchSession();
  }, [code]);

  // Primary WebSocket — only connects after user clicks "Conectar tradução"
  useEffect(() => {
    if (!session || !selectedLang || !userReady) return;

    let keepAliveInterval = null;
    let reconnectTimeout = null;
    let intentionalClose = false;
    let recoveryInterval = null;

    const connect = () => {
      setConnecting(true);
      const ws = new WebSocket(`${WS_URL}/api/ws/participant/${session.id}/${selectedLang}${monitorMode ? '?monitor=1' : ''}`);
      wsRef.current = ws;
      let lastMessageTime = Date.now();
      let lastContentTime = 0;
      let verifyPending = false;
      let healthCheckInterval = null;
      let initialVerifyTimeout = null;

      ws.onopen = () => {
        setConnected(true);
        lastMessageTime = Date.now();
        lastContentTime = 0;
        verifyPending = false;

        // IMMEDIATE verify on connect — confirm we're in the broadcast list
        try {
          ws.send(JSON.stringify({ type: 'verify' }));
          verifyPending = true;
        } catch (e) {}

        // If no verify_result in 5s, force reconnect
        initialVerifyTimeout = setTimeout(() => {
          if (verifyPending && ws.readyState === WebSocket.OPEN) {
            console.log('No verify_result in 5s, reconnecting...');
            ws.close();
          }
        }, 5000);

        // Keepalive every 10s
        keepAliveInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('keepalive');
        }, 10000);
        // Health check: if no message in 15s, connection is dead
        healthCheckInterval = setInterval(() => {
          if (Date.now() - lastMessageTime > 15000 && ws.readyState === WebSocket.OPEN) {
            console.log('No server message in 15s, reconnecting...');
            ws.close();
          }
        }, 5000);
        // Auto-recovery: check every 8s, verify at 12s, reconnect at 20s
        recoveryInterval = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN) return;
          const now = Date.now();
          const timeSinceContent = lastContentTime > 0 ? (now - lastContentTime) : (now - lastMessageTime);
          
          if (timeSinceContent > 12000 && !verifyPending) {
            console.log('No content in 12s, sending verify...');
            verifyPending = true;
            try { ws.send(JSON.stringify({ type: 'verify' })); } catch (e) {}
          } else if (timeSinceContent > 20000) {
            console.log('No content in 20s, reconnecting...');
            ws.close();
          }
        }, 8000);
      };

      ws.onmessage = (event) => {
        lastMessageTime = Date.now();
        try {
          const data = JSON.parse(event.data);
          // P0 isolation: discard events that are not for this session
          if (data.session_id && data.session_id !== session.id) return;
          if (data.type === 'ping') return;
          if (data.type === 'ack_config') {
            ackEnabledRef.current = !!data.enabled;
            return;
          }
          if (data.type === 'service_status') {
            if (!data.lang || data.lang === selectedLang) {
              setAudioServiceSuspended(data.audio_available === false);
            }
            return;
          }
          if (data.type === 'demo_full') {
            demoFullRef.current = true;
            setDemoFull(true);
            setConnected(false);
            ws.close();
            return;
          }
          if (data.type === 'session_ended') {
            setSessionEnded(true);
            setTransmissionPaused(false);
            setConnected(false);
            ws.close();
            return;
          }
          if (data.type === 'transmission_paused') {
            // Grace period — don't show "paused" for micro-reconnections (<4s)
            if (transmissionPausedTimerRef.current) clearTimeout(transmissionPausedTimerRef.current);
            transmissionPausedTimerRef.current = setTimeout(() => {
              setTransmissionPaused(true);
            }, 4000);
            return;
          }
          if (data.type === 'flush_audio_queue') {
            const mode = data.mode || 'clear_all';
            if (mode === 'clear_all') {
              audioQueueRef.current = [];
              isPlayingRef.current = false;
              playingChunkIdsRef.current = [];
              setPlayingChunkIds([]);
              if (audioElementRef.current) {
                audioElementRef.current.pause();
                audioElementRef.current.src = '';
              }
            } else if (mode === 'clear_pending' || mode === 'keep_current') {
              // Spec v3.1: Keep currently playing audio, clear pending queue only
              audioQueueRef.current = [];
            }
            return;
          }
          if (data.type === 'speaker_changed') {
            setCurrentSpeaker(data.speaker || '');
            return;
          }
          if (data.type === 'transmission_resumed') {
            if (transmissionPausedTimerRef.current) clearTimeout(transmissionPausedTimerRef.current);
            setTransmissionPaused(false);
            return;
          }
          if (data.type === 'clear_subtitles') {
            setSubtitles([]);
            setSubtitleCount(0);
            return;
          }
          if (data.type === 'settings_update') {
            setSessionSettings(data.settings);
            lastContentTime = Date.now();
            return;
          }
          if (data.type === 'host_activity') {
            setHostSpeaking(data.is_speaking);
            lastContentTime = Date.now();
            if (hostSpeakingTimeoutRef.current) clearTimeout(hostSpeakingTimeoutRef.current);
            if (data.is_speaking) {
              hostSpeakingTimeoutRef.current = setTimeout(() => setHostSpeaking(false), 5000);
            }
            return;
          }
          if (data.type === 'verify_result') {
            verifyPending = false;
            lastContentTime = Date.now();
            setConnecting(false);
            if (initialVerifyTimeout) { clearTimeout(initialVerifyTimeout); initialVerifyTimeout = null; }
            if (data.was_missing) {
              console.log('Connection re-added to broadcast list by server');
            }
            return;
          }
          if (data.type === 'subtitle') {
            lastContentTime = Date.now();
            setConnecting(false);
            // Cancel pause grace timer — data flowing proves connection is alive
            if (transmissionPausedTimerRef.current) {
              clearTimeout(transmissionPausedTimerRef.current);
              transmissionPausedTimerRef.current = null;
            }
            setTransmissionPaused(false);
            // Phase 1: clear interim only if current displayed utterance is covered
            const covered = data.covered_utterance_ids || [];
            const iState = interimStateRef.current;
            if (covered.length === 0 || covered.includes(iState.currentDisplayUtteranceId)) {
              setInterimText(null);
              iState.currentDisplayUtteranceId = null;
              iState.hasTranslatedForCurrent = false;
              iState.lastTranslatedRevisionForCurrent = 0;
            }
            setSubtitles((prev) => {
              if (data.is_catchup && prev.some(s => s.timestamp === data.timestamp)) {
                return prev;
              }
              return [
                ...prev,
                {
                  id: Date.now() + Math.random(),
                  original: data.original,
                  translated: data.translated,
                  timestamp: new Date(data.timestamp),
                  latency: data.latency,
                  chunk_id: data.chunk_id || null,
                  speaker: data.speaker || '',
                  is_original: !!data.is_original,
                },
              ];
            });
            setLastActivity(new Date());
            if (!data.is_catchup) {
              setSubtitleCount(c => c + 1);
              if (data.chunk_id) {
                sendE2EAck('subtitle_received', data.chunk_id);
                requestAnimationFrame(() => requestAnimationFrame(() => sendE2EAck('subtitle_rendered', data.chunk_id)));
              }
            }
            if (!firstSubtitleRef.current) {
              firstSubtitleRef.current = true;
            }
          } else if (data.type === 'interim_translated') {
            // Phase 1: translated interim — overrides original interim
            lastContentTime = Date.now();
            setConnecting(false);
            const iState = interimStateRef.current;
            if (data.utterance_id !== iState.currentDisplayUtteranceId) return;
            if (data.revision <= iState.lastTranslatedRevisionForCurrent) return;
            iState.hasTranslatedForCurrent = true;
            iState.lastTranslatedRevisionForCurrent = data.revision;
            setInterimText(data.text);
            if (!firstSubtitleRef.current) {
              firstSubtitleRef.current = true;
            }
          } else if (data.type === 'interim_subtitle') {
            lastContentTime = Date.now();
            setConnecting(false);
            const iState = interimStateRef.current;
            // If new utterance, reset state
            if (data.utterance_id && data.utterance_id !== iState.currentDisplayUtteranceId) {
              iState.currentDisplayUtteranceId = data.utterance_id;
              iState.hasTranslatedForCurrent = false;
              iState.lastTranslatedRevisionForCurrent = 0;
            }
            // Only show original if no translated interim exists yet for this utterance
            if (!iState.hasTranslatedForCurrent) {
              setInterimText(data.text);
            }
            if (!firstSubtitleRef.current) {
              firstSubtitleRef.current = true;
            }
          } else if (data.type === 'tts_audio' && audioEnabledRef.current && !overlayMode) {
            // Phase 2+4: Pass chunk correlation for highlight/scroll
            const chunkIds = data.chunk_ids || (data.chunk_id ? [data.chunk_id] : []);
            sendE2EAck('audio_received', chunkIds[0]);
            queueAudio(data.audio, chunkIds, data.audio_format || 'mp3');
          }
        } catch (e) {}
      };

      ws.onerror = () => { setConnected(false); setConnecting(false); };
      ws.onclose = (event) => {
        setConnected(false);
        if (keepAliveInterval) { clearInterval(keepAliveInterval); keepAliveInterval = null; }
        if (healthCheckInterval) { clearInterval(healthCheckInterval); healthCheckInterval = null; }
        if (recoveryInterval) { clearInterval(recoveryInterval); recoveryInterval = null; }
        if (initialVerifyTimeout) { clearTimeout(initialVerifyTimeout); initialVerifyTimeout = null; }
        // Don't reconnect if session ended (reason="Session ended" or code 1000 from server)
        if (!intentionalClose && !demoFullRef.current && event.reason !== 'Session ended') {
          setConnecting(true);
          reconnectTimeout = setTimeout(connect, 2000);
        }
      };
    };

    connect();
    return () => {
      intentionalClose = true;
      if (keepAliveInterval) clearInterval(keepAliveInterval);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (recoveryInterval) clearInterval(recoveryInterval);
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.close();
    };
  }, [session, selectedLang, userReady]);

  // Second WebSocket for interpreter mode (with auto-recovery)
  useEffect(() => {
    if (!session || !secondLang || !interpreterMode) return;

    let keepAliveInterval2 = null;
    let reconnectTimeout2 = null;
    let intentionalClose2 = false;
    let recoveryInterval2 = null;

    const connect2 = () => {
      const ws2 = new WebSocket(`${WS_URL}/api/ws/participant/${session.id}/${secondLang}${monitorMode ? '?monitor=1' : ''}`);
      wsRef2.current = ws2;
      let lastMessageTime2 = Date.now();
      let lastContentTime2 = 0;

      ws2.onopen = () => {
        setConnected2(true);
        lastMessageTime2 = Date.now();
        lastContentTime2 = 0;
        keepAliveInterval2 = setInterval(() => {
          if (ws2.readyState === WebSocket.OPEN) ws2.send('keepalive');
        }, 10000);
        // Auto-recovery for second WS
        recoveryInterval2 = setInterval(() => {
          if (ws2.readyState !== WebSocket.OPEN) return;
          const timeSinceContent = lastContentTime2 > 0 ? (Date.now() - lastContentTime2) : (Date.now() - lastMessageTime2);
          if (timeSinceContent > 20000) {
            try { ws2.send(JSON.stringify({ type: 'verify' })); } catch (e) {}
          }
          if (timeSinceContent > 35000) { ws2.close(); }
        }, 12000);
      };

      ws2.onmessage = (event) => {
        lastMessageTime2 = Date.now();
        try {
          const data = JSON.parse(event.data);
          // P0 isolation: discard events that are not for this session
          if (data.session_id && data.session_id !== session.id) return;
          if (data.type === 'ping') return;
          if (data.type === 'session_ended') {
            setSessionEnded(true);
            setTransmissionPaused(false);
            setConnected2(false);
            ws2.close();
            return;
          }
          if (data.type === 'transmission_paused') {
            if (transmissionPausedTimerRef.current) clearTimeout(transmissionPausedTimerRef.current);
            transmissionPausedTimerRef.current = setTimeout(() => {
              setTransmissionPaused(true);
            }, 4000);
            return;
          }
          if (data.type === 'flush_audio_queue') {
            const mode = data.mode || 'clear_all';
            if (mode === 'clear_all') {
              audioQueueRef.current = [];
              isPlayingRef.current = false;
              playingChunkIdsRef.current = [];
              setPlayingChunkIds([]);
              if (audioElementRef.current) {
                audioElementRef.current.pause();
                audioElementRef.current.src = '';
              }
            } else if (mode === 'clear_pending' || mode === 'keep_current') {
              audioQueueRef.current = [];
            }
            return;
          }
          if (data.type === 'transmission_resumed') {
            if (transmissionPausedTimerRef.current) clearTimeout(transmissionPausedTimerRef.current);
            setTransmissionPaused(false);
            return;
          }
          if (data.type === 'verify_result') { lastContentTime2 = Date.now(); return; }
          if (data.type === 'clear_subtitles') {
            setSubtitles2([]);
            return;
          }
          if (data.type === 'subtitle') {
            lastContentTime2 = Date.now();
            const covered = data.covered_utterance_ids || [];
            const iState2 = interimState2Ref.current;
            if (covered.length === 0 || covered.includes(iState2.currentDisplayUtteranceId)) {
              setInterimText2(null);
              iState2.currentDisplayUtteranceId = null;
              iState2.hasTranslatedForCurrent = false;
              iState2.lastTranslatedRevisionForCurrent = 0;
            }
            setSubtitles2((prev) => [
              ...prev,
              {
                id: Date.now() + Math.random() + 1000,
                original: data.original,
                translated: data.translated,
                timestamp: new Date(data.timestamp),
                latency: data.latency,
                chunk_id: data.chunk_id || null,
                is_original: !!data.is_original,
              },
            ]);
          } else if (data.type === 'interim_translated') {
            lastContentTime2 = Date.now();
            const iState2 = interimState2Ref.current;
            if (data.utterance_id !== iState2.currentDisplayUtteranceId) return;
            if (data.revision <= iState2.lastTranslatedRevisionForCurrent) return;
            iState2.hasTranslatedForCurrent = true;
            iState2.lastTranslatedRevisionForCurrent = data.revision;
            setInterimText2(data.text);
          } else if (data.type === 'interim_subtitle') {
            lastContentTime2 = Date.now();
            const iState2 = interimState2Ref.current;
            if (data.utterance_id && data.utterance_id !== iState2.currentDisplayUtteranceId) {
              iState2.currentDisplayUtteranceId = data.utterance_id;
              iState2.hasTranslatedForCurrent = false;
              iState2.lastTranslatedRevisionForCurrent = 0;
            }
            if (!iState2.hasTranslatedForCurrent) {
              setInterimText2(data.text);
            }
          }
        } catch (e) {
          // Ignore non-JSON messages
        }
      };

      ws2.onerror = () => setConnected2(false);
      ws2.onclose = () => {
        setConnected2(false);
        if (keepAliveInterval2) { clearInterval(keepAliveInterval2); keepAliveInterval2 = null; }
        if (recoveryInterval2) { clearInterval(recoveryInterval2); recoveryInterval2 = null; }
        if (!intentionalClose2) reconnectTimeout2 = setTimeout(connect2, 2000);
      };
    };

    connect2();
    return () => {
      intentionalClose2 = true;
      if (keepAliveInterval2) clearInterval(keepAliveInterval2);
      if (reconnectTimeout2) clearTimeout(reconnectTimeout2);
      if (recoveryInterval2) clearInterval(recoveryInterval2);
      if (wsRef2.current?.readyState === WebSocket.OPEN) wsRef2.current.close();
    };
  }, [session, secondLang, interpreterMode]);

  // Auto-scroll to latest subtitle
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [subtitles]);

  useEffect(() => {
    if (scrollRef2.current) {
      scrollRef2.current.scrollTo({ top: scrollRef2.current.scrollHeight, behavior: 'smooth' });
    }
  }, [subtitles2]);

  // Last activity timer
  const [activityAgo, setActivityAgo] = useState('');
  useEffect(() => {
    if (!lastActivity) return;
    const interval = setInterval(() => {
      const secs = Math.floor((Date.now() - lastActivity.getTime()) / 1000);
      if (secs < 5) setActivityAgo('agora');
      else if (secs < 60) setActivityAgo(`${secs}s`);
      else setActivityAgo(`${Math.floor(secs / 60)}m`);
    }, 1000);
    return () => clearInterval(interval);
  }, [lastActivity]);

  const toggleInterpreterMode = () => {
    if (!interpreterMode && session.target_langs.filter(l => session.bilateral || l !== session.source_lang).length > 1) {
      const available = session.target_langs.filter((l) => l !== selectedLang && (session.bilateral || l !== session.source_lang));
      setSecondLang(available[0]);
      setInterpreterMode(true);
    } else {
      setInterpreterMode(false);
      setSecondLang(null);
      setSubtitles2([]);
      setConnected2(false);
    }
  };

  const handleConnect = () => {
    unlockAudio();
    setUserReady(true);
  };

  // Auto-disable modes if host disables them remotely
  useEffect(() => {
    if (!sessionSettings.teleprompter_enabled && teleprompterMode) {
      setTeleprompterMode(false);
    }
    if (!sessionSettings.interpreter_enabled && interpreterMode) {
      setInterpreterMode(false);
      setSecondLang(null);
      setSubtitles2([]);
      setConnected2(false);
    }
  }, [sessionSettings]);

  // iOS-compatible audio system: single reusable Audio element + Blob URLs
  useEffect(() => {
    const audio = new Audio();
    audio.playsInline = true;
    audioElementRef.current = audio;
    return () => {
      audio.pause();
      audio.src = '';
    };
  }, []);

  // Unlock audio on iOS — uses a SEPARATE temporary element to not interrupt TTS
  const unlockAudio = () => {
    if (audioUnlockedRef.current) return;
    try {
      const tempAudio = new Audio('data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRwMHAAAAAAD/+1DEAAAHAAL0AAAAIgAAXoAAAARMQU1FMy4xMDBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVQ==');
      tempAudio.playsInline = true;
      tempAudio.play().then(() => {
        audioUnlockedRef.current = true;
        tempAudio.pause();
        tempAudio.remove();
      }).catch(() => {});
    } catch (e) {}
  };

  const base64ToBlob = (base64, mimeType) => {
    const byteChars = atob(base64);
    const byteArrays = [];
    for (let offset = 0; offset < byteChars.length; offset += 512) {
      const slice = byteChars.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: mimeType });
  };

  const sendE2EAck = (ack, chunkId) => {
    try {
      const ws = wsRef.current;
      if (!ackEnabledRef.current || !chunkId || !ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: 'e2e_ack', ack, chunk_id: chunkId, client_ts: Date.now() / 1000 }));
    } catch (e) {}
  };

  const playNextAudio = () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      // Phase 4: Clear playing state when queue empties
      playingChunkIdsRef.current = [];
      setPlayingChunkIds([]);
      return;
    }
    isPlayingRef.current = true;
    const entry = audioQueueRef.current.shift();
    const nextBase64 = entry.audio;
    const chunkIds = entry.chunkIds || [];
    const audioMime = entry.format === 'wav' ? 'audio/wav' : 'audio/mp3';
    const audio = audioElementRef.current;
    if (!audio) { isPlayingRef.current = false; return; }

    // Phase 4: Set currently playing chunks for highlight (skip for mic — no chunk correlation)
    playingChunkIdsRef.current = chunkIds;
    if (chunkIds.length > 0) {
      setPlayingChunkIds([...chunkIds]);
    }

    // Highlight only — no scroll to playing segment (prevents bounce in continuous streams)

    // Phase 1: Instrumentation — log queue depth and playback start
    if (audioQueueRef.current.length > 2) {
      console.log(`[TTS-Play] start chunks=[${chunkIds.join(',')}] queue_remaining=${audioQueueRef.current.length}`);
    }

    // Catch-up adaptativo: acelera ligeiramente quando a fila cresce (recupera atraso ao vivo)
    const backlog = audioQueueRef.current.length;
    const catchUpRate = backlog >= 4 ? 1.12 : backlog >= 2 ? 1.06 : 1.0;

    // Spec v3.1: Estimate duration from base64 size (MP3 ~128kbps)
    const estimatedDuration = Math.round((nextBase64.length * 0.75) / 16000);
    const sendPlaybackAck = (status, duration) => {
      try {
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
          const ack = { type: 'tts_playback_ack', status, chunk_ids: chunkIds };
          if (duration) ack.estimated_duration = duration;
          ws.send(JSON.stringify(ack));
        }
      } catch (e) {}
    };

    try {
      const blob = base64ToBlob(nextBase64, audioMime);
      const blobUrl = URL.createObjectURL(blob);
      
      sendPlaybackAck('started', estimatedDuration);
      sendE2EAck('playback_started', chunkIds[0]);
      
      const cleanup = () => {
        URL.revokeObjectURL(blobUrl);
        audio.onended = null;
        audio.onerror = null;
        sendPlaybackAck('finished');
        sendE2EAck('playback_finished', chunkIds[0]);
        playNextAudio();
      };
      
      audio.onended = cleanup;
      audio.onerror = cleanup;
      audio.src = blobUrl;
      audio.defaultPlaybackRate = catchUpRate;
      audio.playbackRate = catchUpRate;
      if (catchUpRate > 1.0) {
        console.log(`[TTS-Play] catch-up rate=${catchUpRate} backlog=${backlog}`);
      }
      audio.play().catch(cleanup);
    } catch (e) {
      isPlayingRef.current = false;
      sendPlaybackAck('finished');
      playNextAudio();
    }
  };

  const queueAudio = (audioBase64, chunkIds = [], format = 'mp3') => {
    audioQueueRef.current.push({ audio: audioBase64, chunkIds, format });
    // Phase 1: Log when queue depth grows significantly
    if (audioQueueRef.current.length > 3) {
      console.log(`[TTS-Queue] depth=${audioQueueRef.current.length} new_chunks=[${chunkIds.join(',')}]`);
    }
    if (!isPlayingRef.current) playNextAudio();
  };

  const toggleAudio = () => {
    // Unlock audio on user gesture (critical for iOS)
    unlockAudio();
    setAudioEnabled((prev) => {
      const next = !prev;
      audioEnabledRef.current = next;
      if (!next) {
        audioQueueRef.current = [];
        isPlayingRef.current = false;
        if (audioElementRef.current) {
          audioElementRef.current.pause();
          audioElementRef.current.src = '';
        }
      }
      return next;
    });
  };

  // Theme colors
  const bg = darkMode ? 'bg-slate-950' : 'bg-white';
  const headerBg = darkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-200';
  const cardBg = darkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-200';
  const textPrimary = darkMode ? 'text-white' : 'text-slate-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-slate-500';
  const textMuted = darkMode ? 'text-slate-500' : 'text-slate-400';
  const borderColor = darkMode ? 'border-slate-800' : 'border-slate-200';
  const selectBg = darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300 text-slate-900';
  const btnGhost = darkMode ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100';
  const btnActive = 'bg-blue-600 hover:bg-blue-700 text-white';
  const accentColor = darkMode ? 'text-blue-400' : 'text-blue-600';
  const footerBg = darkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-200';

  if (loading) {
    return (
      <div className={`min-h-screen ${bg} flex items-center justify-center`}>
        <div className="text-center">
          <Loader2 className={`w-12 h-12 animate-spin ${accentColor} mx-auto mb-4`} />
          <p className={`font-['Inter'] text-lg ${textSecondary}`}>{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${bg} flex items-center justify-center`}>
        <Card className={`max-w-md p-8 text-center ${cardBg}`}>
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className={`font-['Manrope'] text-2xl font-bold ${textPrimary} mb-2`}>{t.error}</h2>
          <p className={`font-['Inter'] ${textSecondary} mb-6`}>{error}</p>
          <Button onClick={() => navigate('/')} className={btnActive} data-testid="back-home-error-btn">
            {t.backToHome}
          </Button>
        </Card>
      </div>
    );
  }

  const currentFontSize = FONT_SIZES[fontSize];
  const lastSubtitle = subtitles[subtitles.length - 1];

  if (demoFull) {
    return (
      <div className="h-screen bg-slate-950 flex items-center justify-center">
        <Card className="bg-slate-900 border-slate-800 max-w-md w-full mx-4 text-center" data-testid="demo-full-card">
          <CardContent className="pt-8 pb-6 px-6">
            <Users className="w-16 h-16 text-orange-400 mx-auto mb-4" />
            <h2 className="font-['Manrope'] text-xl font-bold text-white mb-2">Demonstração cheia</h2>
            <p className="font-['Inter'] text-sm text-slate-400">
              Esta sessão de demonstração atingiu o limite de participantes. Tente novamente mais tarde ou contacte o organizador.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (sessionEnded) {
    if (overlayMode) {
      const bgColor = overlayBgColors[overlayBg] || overlayBgColors.green;
      return (
        <div data-testid="overlay-ended" style={{
          position: 'fixed', inset: 0,
          backgroundColor: bgColor,
          overflow: 'hidden',
        }} />
      );
    }
    return (
      <div className="h-screen bg-slate-950 flex items-center justify-center">
        <Card className="bg-slate-900 border-slate-800 max-w-md w-full mx-4 text-center" data-testid="session-ended-card">
          <CardContent className="pt-8 pb-6 px-6">
            <CircleStop className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h2 className="font-['Manrope'] text-xl font-bold text-white mb-2">Sessao Encerrada</h2>
            <p className="font-['Inter'] text-sm text-slate-400 mb-1">
              {session?.name && <span className="text-slate-300 font-medium">{session.name}</span>}
              {session?.name && session?.code && <span> &middot; </span>}
              {session?.code && <span>Sessao <strong className="text-slate-300">{session.code}</strong></span>}
            </p>
            <p className="font-['Inter'] text-sm text-slate-400 mb-6">
              Esta sessao de traducao foi encerrada pelo anfitriao.
            </p>
            {subtitles.length > 0 && (
              <p className="font-['Inter'] text-xs text-slate-500 mb-4">
                Foram recebidas {subtitles.length} traducoes durante a sessao.
              </p>
            )}
            <Button
              onClick={() => window.location.href = '/'}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="session-ended-home-btn"
            >
              Voltar ao inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }


  // === AV Overlay Mode: dedicated minimal render ===
  if (overlayMode) {
    const bgColor = overlayBgColors[overlayBg] || overlayBgColors.green;
    const lastSub = subtitles[subtitles.length - 1];
    const displayText = lastSub?.translated || '';

    return (
      <div
        data-testid="overlay-container"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: bgColor,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          margin: 0,
          padding: 0,
        }}
      >
        <div style={{
          width: '100%',
          maxWidth: '90%',
          paddingBottom: '5vh',
          paddingLeft: '5vw',
          paddingRight: '5vw',
          textAlign: 'center',
        }}>
          {displayText ? (
            <p
              data-testid="overlay-subtitle"
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 'clamp(24px, 4vw, 48px)',
                fontWeight: 700,
                color: '#FFFFFF',
                WebkitTextStroke: overlayBg === 'black' ? '0px' : '2px #000000',
                paintOrder: 'stroke fill',
                lineHeight: 1.3,
                margin: 0,
                textShadow: overlayBg === 'black'
                  ? '0 2px 8px rgba(0,0,0,0.8)'
                  : '0 2px 4px rgba(0,0,0,0.5)',
              }}
            >
              {displayText}
            </p>
          ) : (
            <div data-testid="overlay-waiting" style={{ height: '1px' }} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen ${bg} flex flex-col overflow-hidden transition-colors duration-300`}>
      {/* Bloco B: discreet notice — translated audio suspended, subtitles keep working */}
      {audioServiceSuspended && (
        <div
          data-testid="audio-service-suspended-banner"
          className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 px-4 py-2 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-['Inter'] backdrop-blur flex items-center gap-2 max-w-[90vw] text-center"
        >
          <VolumeX className="w-3.5 h-3.5 shrink-0" />
          {uiLang === 'pt'
            ? 'Áudio traduzido temporariamente indisponível — as legendas continuam ativas.'
            : 'Translated audio temporarily unavailable — subtitles remain active.'}
        </div>
      )}
      {/* Header */}
      <div className={`${headerBg} border-b px-3 ${monitorMode ? 'py-1.5' : 'py-3'} sticky top-0 z-10`}>
        <div className="max-w-7xl mx-auto">
          {monitorMode ? (
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-cyan-500/50 bg-cyan-500/10 text-cyan-400 tracking-wider" data-testid="participant-monitor-badge">MONITOR</span>
              <div className={`w-2 h-2 rounded-full ${connected ? (hostSpeaking ? 'bg-blue-500 animate-pulse' : 'bg-green-500') : (connecting ? 'bg-yellow-500 animate-pulse' : 'bg-red-500')}`} data-testid="connection-indicator" />
              <span className={`font-['Inter'] text-[11px] font-medium ${textSecondary}`} data-testid="connection-status">
                {LANGUAGES.find((l) => l.code === selectedLang)?.name || selectedLang || ''}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={toggleAudio}
                className={`ml-auto h-6 w-6 p-0 ${audioEnabled ? btnActive : btnGhost}`}
                title={audioEnabled ? t.soundOn : t.soundOff}
                data-testid="audio-toggle"
              >
                {audioEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              </Button>
            </div>
          ) : (
          <>
          {/* Top row - Title + Status */}
          <div className="flex justify-between items-center mb-2">
            <div>
              <h1 className={`font-['Manrope'] text-lg font-bold ${textPrimary}`} data-testid="participant-view-title">
                {t.liveTranslation}
                {teleprompterMode && <span className={`ml-2 text-sm font-normal ${accentColor}`}>(Teleprompter)</span>}
              </h1>
              <p className={`font-['Inter'] text-xs ${textSecondary}`}>
                {session.name && <span className={`font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`} data-testid="session-name-display">{session.name} &middot; </span>}
                {t.session}: <span className={`font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`} data-testid="session-code-display">{session.code}</span>
                {session.is_demo && <span className="ml-2 inline-flex items-center rounded-full border border-orange-500/50 bg-orange-500/15 text-orange-400 text-[9px] font-bold px-2 py-0.5 tracking-widest align-middle" data-testid="participant-demo-badge">DEMO</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {userReady ? (
                <>
                  <div className={`w-2 h-2 rounded-full ${connected ? (hostSpeaking ? 'bg-blue-500 animate-pulse' : 'bg-green-500') : (connecting ? 'bg-yellow-500 animate-pulse' : 'bg-red-500')}`} data-testid="connection-indicator" />
                  <span className={`font-['Inter'] text-xs ${hostSpeaking ? accentColor + ' font-medium' : textSecondary}`} data-testid="connection-status">
                    {hostSpeaking ? t.speakerActive : connecting ? t.connectingTranslation : (connected ? t.connected : t.disconnected)}
                  </span>
                </>
              ) : (
                <span className={`font-['Inter'] text-xs ${textMuted}`}>{t.session}: {session.code}</span>
              )}
              {lastActivity && (
                <span className={`font-['Inter'] text-xs ${activityAgo === 'agora' ? accentColor : textMuted}`} data-testid="last-activity">
                  {activityAgo}
                </span>
              )}
            </div>
          </div>

          {/* Controls row — only visible when connected */}
          {userReady && connected && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Translation Language - always visible, allows switching */}
            <Select value={selectedLang || ''} onValueChange={(val) => { unlockAudio(); setSelectedLang(val); }}>
              <SelectTrigger className={`w-36 h-8 text-xs ${selectBg}`} data-testid="language-selector">
                <SelectValue placeholder={t.chooseLang} />
              </SelectTrigger>
              <SelectContent className={darkMode ? 'bg-slate-800 border-slate-700' : ''}>
                {session.target_langs
                  .filter((langCode) => session.bilateral || langCode !== session.source_lang)
                  .map((langCode) => {
                    const lang = LANGUAGES.find((l) => l.code === langCode);
                    return (
                      <SelectItem key={langCode} value={langCode} className={darkMode ? 'text-white hover:bg-slate-700' : ''}>
                        <span className="inline-flex items-center gap-2">
                          {flagClass(langCode) && <span className={flagClass(langCode)} aria-hidden="true"></span>}
                          {lang?.name || langCode}
                        </span>
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>

            {/* Interpreter mode — only if host enabled it and 2+ target langs */}
            {selectedLang && session.target_langs.filter(l => session.bilateral || l !== session.source_lang).length > 1 && sessionSettings.interpreter_enabled && (
              <Button
                size="sm"
                variant="ghost"
                onClick={toggleInterpreterMode}
                className={`h-7 text-xs px-2 ${interpreterMode ? btnActive : btnGhost}`}
                data-testid="interpreter-mode-toggle"
              >
                {interpreterMode ? t.modeActive : t.interpreterMode}
              </Button>
            )}

            {interpreterMode && secondLang && (
              <Select value={secondLang} onValueChange={setSecondLang}>
                <SelectTrigger className={`w-28 h-7 text-xs ${selectBg}`} data-testid="second-language-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={darkMode ? 'bg-slate-800 border-slate-700' : ''}>
                  {session.target_langs.filter(l => l !== selectedLang && (session.bilateral || l !== session.source_lang)).map((langCode) => {
                    const lang = LANGUAGES.find((l) => l.code === langCode);
                    return (
                      <SelectItem key={langCode} value={langCode} className={darkMode ? 'text-white hover:bg-slate-700' : ''}>
                        <span className="inline-flex items-center gap-2">
                          {flagClass(langCode) && <span className={flagClass(langCode)} aria-hidden="true"></span>}
                          {lang?.name || langCode}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}

            <div className={`w-px h-5 ${darkMode ? 'bg-slate-700' : 'bg-slate-300'}`} />

            {/* Show/Hide Original */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowOriginal(!showOriginal)}
              className={`h-7 text-xs px-2 ${showOriginal ? btnActive : btnGhost}`}
              title={showOriginal ? t.hideOriginal : t.showOriginal}
              data-testid="toggle-original-btn"
            >
              {showOriginal ? <Eye className="w-3.5 h-3.5 mr-1" /> : <EyeOff className="w-3.5 h-3.5 mr-1" />}
              Original
            </Button>

            {/* Teleprompter — only if host enabled it */}
            {sessionSettings.teleprompter_enabled && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setTeleprompterMode(!teleprompterMode)}
                className={`h-7 text-xs px-2 ${teleprompterMode ? btnActive : btnGhost}`}
                data-testid="teleprompter-toggle"
              >
                {teleprompterMode ? <Minimize className="w-3.5 h-3.5 mr-1" /> : <Maximize className="w-3.5 h-3.5 mr-1" />}
                {teleprompterMode ? t.normalMode : t.teleprompter}
              </Button>
            )}

            {/* Font size */}
            <div className="flex items-center gap-0.5">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setFontSize(Math.max(0, fontSize - 1))}
                className={`h-7 w-7 p-0 ${btnGhost}`}
                disabled={fontSize === 0}
                data-testid="font-decrease-btn"
              >
                <Minus className="w-3.5 h-3.5" />
              </Button>
              <span className={`font-['Inter'] text-xs font-bold w-6 text-center ${textSecondary}`}>
                {FONT_SIZES[fontSize].label}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setFontSize(Math.min(3, fontSize + 1))}
                className={`h-7 w-7 p-0 ${btnGhost}`}
                disabled={fontSize === 3}
                data-testid="font-increase-btn"
              >
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>

            {/* Audio */}
            <Button
              size="sm"
              variant="ghost"
              onClick={toggleAudio}
              className={`h-7 text-xs px-2 ${audioEnabled ? btnActive : btnGhost}`}
              data-testid="audio-toggle"
            >
              {audioEnabled ? <Volume2 className="w-3.5 h-3.5 mr-1" /> : <VolumeX className="w-3.5 h-3.5 mr-1" />}
              {audioEnabled ? t.soundOn : t.soundOff}
            </Button>

            {/* Theme toggle */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDarkMode(!darkMode)}
              className={`h-7 w-7 p-0 ${btnGhost}`}
              data-testid="theme-toggle"
            >
              {darkMode ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </Button>
          </div>
          )}
          </>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden">
        {!userReady ? (
          /* PRE-CONNECT STATE — language selection + connect button */
          <div className="h-full flex items-center justify-center px-6" data-testid="welcome-view">
            <div className="text-center max-w-md space-y-6">
              <div className={`w-16 h-16 mx-auto rounded-2xl ${darkMode ? 'bg-blue-950/50 border border-blue-800' : 'bg-blue-50 border border-blue-200'} flex items-center justify-center`}>
                <Volume2 className={`w-8 h-8 ${accentColor}`} />
              </div>
              <div>
                {session.name && (
                  <p className={`font-['Inter'] text-sm font-medium ${accentColor} mb-1`} data-testid="event-name-welcome">{session.name}</p>
                )}
                <h2 className={`font-['Manrope'] text-2xl font-bold ${textPrimary} mb-2`} data-testid="welcome-title">
                  {t.welcomeTitle}
                </h2>
                <p className={`font-['Inter'] text-base ${textSecondary}`}>
                  {t.welcomeText}
                </p>
              </div>

              {/* Language selector (only if multiple target langs) */}
              {session.target_langs.filter(l => session.bilateral || l !== session.source_lang).length > 1 && (
                <div className="flex justify-center">
                  <Select value={selectedLang || ''} onValueChange={setSelectedLang}>
                    <SelectTrigger className={`w-48 h-10 text-sm ${selectBg}`} data-testid="language-selector-welcome">
                      <SelectValue placeholder={t.chooseLang} />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-slate-800 border-slate-700' : ''}>
                      {session.target_langs
                        .filter((langCode) => session.bilateral || langCode !== session.source_lang)
                        .map((langCode) => {
                          const lang = LANGUAGES.find((l) => l.code === langCode);
                          return (
                            <SelectItem key={langCode} value={langCode} className={darkMode ? 'text-white hover:bg-slate-700' : ''}>
                              <span className="inline-flex items-center gap-2">
                                {flagClass(langCode) && <span className={flagClass(langCode)} aria-hidden="true"></span>}
                                {lang?.name || langCode}
                              </span>
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Bloco C: sound test (cached audio) */}
              <button
                onClick={playSoundTest}
                disabled={!selectedLang || soundTestState === 'loading' || soundTestState === 'playing'}
                className={`w-48 h-10 mb-3 rounded-xl font-['Inter'] text-sm font-medium border transition-all ${
                  !selectedLang
                    ? `${darkMode ? 'border-slate-800 text-slate-600' : 'border-slate-200 text-slate-400'} cursor-not-allowed`
                    : soundTestState === 'played'
                      ? 'border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10'
                      : soundTestState === 'error'
                        ? 'border-red-500/50 text-red-500 hover:bg-red-500/10'
                        : `${darkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`
                }`}
                data-testid="sound-test-btn"
              >
                {soundTestState === 'loading' ? stLabels.playing
                  : soundTestState === 'playing' ? stLabels.playing
                  : soundTestState === 'played' ? stLabels.ok
                  : soundTestState === 'error' ? stLabels.err
                  : stLabels.test}
              </button>

              {/* Connect button */}
              <button
                onClick={handleConnect}
                disabled={!selectedLang}
                className={`w-48 h-12 rounded-xl font-['Manrope'] text-base font-semibold transition-all ${
                  selectedLang
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 active:scale-95'
                    : `${darkMode ? 'bg-slate-800 text-slate-500' : 'bg-slate-200 text-slate-400'} cursor-not-allowed`
                }`}
                data-testid="connect-translation-btn"
              >
                {t.connectTranslation}
              </button>

              <p className={`font-['Inter'] text-xs ${textMuted}`}>
                {t.session}: <span className="font-semibold">{session.code}</span>
              </p>
            </div>
          </div>
        ) : connecting && !connected ? (
          /* CONNECTING STATE */
          <div className="h-full flex items-center justify-center px-6" data-testid="connecting-view">
            <div className="text-center space-y-4">
              <Loader2 className={`w-10 h-10 mx-auto animate-spin ${accentColor}`} />
              <p className={`font-['Inter'] text-lg font-medium ${textPrimary}`}>
                {t.connectingTranslation}
              </p>
            </div>
          </div>
        ) : teleprompterMode ? (
          /* TELEPROMPTER MODE */
          <div className="h-full flex items-center justify-center px-8" data-testid="teleprompter-view">
            {lastSubtitle ? (
              <div className="text-center max-w-5xl animate-fade-in">
                {showOriginal && (
                  <p className={`font-['Inter'] ${FONT_SIZES[Math.max(0, fontSize - 1)].text} ${textSecondary} mb-6 leading-relaxed`} data-testid="teleprompter-original">
                    {lastSubtitle.original}
                  </p>
                )}
                <p className={`font-['Inter'] ${currentFontSize.teleText} ${textPrimary} font-bold leading-tight`} data-testid="teleprompter-translated">
                  {lastSubtitle.translated}
                </p>
                {lastSubtitle.is_original && (
                  <p className={`font-['Inter'] text-xs uppercase tracking-widest mt-3 ${textMuted}`} data-testid="teleprompter-original-tag">
                    idioma original · sem voz
                  </p>
                )}
                {/* Interim: show next chunk being processed below the last final subtitle */}
                {interimText && (
                  <div className="mt-8 animate-fade-in" data-testid="teleprompter-interim">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                      <span className="font-['Inter'] text-xs text-blue-400 uppercase tracking-wide">A traduzir...</span>
                    </div>
                    <p className={`font-['Inter'] ${currentFontSize.text} ${textSecondary} italic leading-relaxed`}>
                      {interimText}
                    </p>
                  </div>
                )}
              </div>
            ) : interimText ? (
              /* No final subtitle yet, but interim available */
              <div className="text-center max-w-5xl animate-fade-in" data-testid="teleprompter-interim-only">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-400 animate-pulse" />
                  <span className={`font-['Inter'] text-sm text-blue-400 uppercase tracking-wide`}>A traduzir...</span>
                </div>
                <p className={`font-['Inter'] ${currentFontSize.teleText} ${textSecondary} italic font-medium leading-tight`}>
                  {interimText}
                </p>
              </div>
            ) : (
              <div className="text-center space-y-4">
                {transmissionPaused ? (
                  <div className="flex flex-col items-center gap-3" data-testid="teleprompter-paused">
                    <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
                    <p className="font-['Inter'] text-xl text-amber-400 font-medium">Transmissao pausada</p>
                    <p className={`font-['Inter'] text-sm ${textMuted}`}>A aguardar o anfitriao...</p>
                  </div>
                ) : hostSpeaking ? (
                  <div className="flex items-center justify-center gap-2 mb-4" data-testid="teleprompter-activity">
                    <span className="flex gap-1">
                      <span className={`w-2 h-2 rounded-full ${accentColor} bg-current animate-bounce`} style={{animationDelay: '0ms'}} />
                      <span className={`w-2 h-2 rounded-full ${accentColor} bg-current animate-bounce`} style={{animationDelay: '150ms'}} />
                      <span className={`w-2 h-2 rounded-full ${accentColor} bg-current animate-bounce`} style={{animationDelay: '300ms'}} />
                    </span>
                    <span className={`font-['Inter'] text-lg ${accentColor} font-medium`}>{t.speakerActive}</span>
                  </div>
                ) : (
                  <p className={`font-['Inter'] text-2xl ${textMuted}`}>
                    {t.waitingSubtitles}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : !interpreterMode ? (
          /* NORMAL SINGLE LANGUAGE MODE */
          <div className="flex flex-col h-full" data-testid="normal-mode-wrapper">
          <div
            ref={scrollRef}
            className="max-w-4xl mx-auto px-4 py-6 flex-1 overflow-y-auto w-full"
            data-testid="subtitles-container"
          >
            {subtitles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 space-y-4">
                {hostSpeaking ? (
                  /* Speaker is active — show translating indicator */
                  <div className="flex flex-col items-center gap-3 animate-fade-in" data-testid="translating-indicator">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-full bg-blue-500 animate-bounce`} style={{animationDelay: '0ms'}} />
                      <span className={`w-2.5 h-2.5 rounded-full bg-blue-500 animate-bounce`} style={{animationDelay: '150ms'}} />
                      <span className={`w-2.5 h-2.5 rounded-full bg-blue-500 animate-bounce`} style={{animationDelay: '300ms'}} />
                    </div>
                    <p className={`font-['Inter'] text-lg font-medium ${accentColor}`}>
                      {t.speakerActive}
                    </p>
                  </div>
                ) : transmissionPaused ? (
                  /* Transmission paused — host disconnected/crash */
                  <div className="flex flex-col items-center gap-3" data-testid="transmission-paused">
                    <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
                    </div>
                    <p className={`font-['Inter'] text-lg font-medium text-amber-400`}>
                      Transmissao pausada
                    </p>
                    <p className={`font-['Inter'] text-sm ${textMuted} text-center max-w-xs`}>
                      A aguardar que o anfitriao retome a transmissao...
                    </p>
                  </div>
                ) : (
                  /* Idle — waiting for speaker */
                  <div className="flex flex-col items-center gap-3" data-testid="waiting-state">
                    <div className={`w-3 h-3 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
                    <p className={`font-['Inter'] text-lg ${textMuted}`} data-testid="waiting-message">
                      {connected ? t.readyToReceive : t.disconnected}
                    </p>
                    {connected && (
                      <p className={`font-['Inter'] text-sm ${textMuted}`}>
                        {t.waitingSubtitles}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {subtitles.map((subtitle) => {
                  const isPlaying = subtitle.chunk_id && playingChunkIds.includes(subtitle.chunk_id);
                  return (
                  <div
                    key={subtitle.id}
                    data-chunk-id={subtitle.chunk_id || undefined}
                    className={`${cardBg} rounded-xl p-5 border animate-fade-in transition-all duration-300 ${isPlaying ? (darkMode ? 'ring-2 ring-blue-500/60 border-blue-500/40' : 'ring-2 ring-blue-400/60 border-blue-400/40') : ''}`}
                    data-testid="subtitle-item"
                  >
                    {subtitle.speaker && (
                      <div className={`font-['Inter'] text-xs font-semibold ${accentColor} mb-2 flex items-center gap-1.5`} data-testid="subtitle-speaker">
                        <span className="w-1 h-1 rounded-full bg-current" />
                        {subtitle.speaker}
                      </div>
                    )}
                    {showOriginal && (
                      <div className="mb-3">
                        <label className={`font-['Inter'] text-xs font-medium ${textMuted} uppercase tracking-wide mb-1 block`}>
                          {t.original}
                        </label>
                        <p className={`font-['Inter'] text-sm ${textSecondary} leading-relaxed`} data-testid="subtitle-original">
                          {subtitle.original}
                        </p>
                      </div>
                    )}
                    <div>
                      {showOriginal && (
                        <label className={`font-['Inter'] text-xs font-medium ${accentColor} uppercase tracking-wide mb-1 block`}>
                          {t.translation}
                        </label>
                      )}
                      <p
                        className={`font-['Inter'] ${currentFontSize.text} ${textPrimary} leading-relaxed font-medium`}
                        data-testid="subtitle-translated"
                      >
                        {subtitle.translated}
                      </p>
                    </div>
                    <div className={`mt-3 pt-2 ${borderColor} border-t flex justify-between items-center`}>
                      <span className={`font-['Inter'] text-xs ${textMuted}`} data-testid="subtitle-timestamp">
                        {subtitle.timestamp.toLocaleTimeString('pt-PT')}
                        {subtitle.speaker && ` · ${subtitle.speaker}`}
                      </span>
                      {subtitle.is_original && (
                        <span className={`font-['Inter'] text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${darkMode ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' : 'bg-cyan-50 text-cyan-700 border-cyan-200'}`} data-testid="original-subtitle-tag">
                          original · sem voz
                        </span>
                      )}
                      {isPlaying && (
                        <span className={`font-['Inter'] text-xs ${darkMode ? 'text-blue-400' : 'text-blue-500'} flex items-center gap-1`} data-testid="playing-indicator">
                          <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                          a reproduzir
                        </span>
                      )}
                    </div>
                  </div>
                  );
                })}
                {/* Live activity indicator at the bottom of subtitle list */}
                {hostSpeaking && (
                  <div className={`flex items-center gap-2 px-5 py-3 animate-fade-in`} data-testid="live-translating-indicator">
                    <span className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{animationDelay: '0ms'}} />
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{animationDelay: '150ms'}} />
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{animationDelay: '300ms'}} />
                    </span>
                    <span className={`font-['Inter'] text-sm ${accentColor}`}>{t.speakerActive}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Fixed interim subtitle at bottom */}
          {interimText && (
            <div
              className={`shrink-0 ${darkMode ? 'bg-slate-900/95 border-slate-700/50' : 'bg-white/95 border-slate-200'} border-t backdrop-blur-sm px-4 py-3`}
              data-testid="interim-subtitle"
            >
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                  <span className={`font-['Inter'] text-xs font-medium text-blue-400 uppercase tracking-wide`}>
                    A traduzir...
                  </span>
                </div>
                <p className={`font-['Inter'] ${currentFontSize.text} ${textSecondary} leading-relaxed italic`}>
                  {interimText}
                </p>
              </div>
            </div>
          )}
          </div>
        ) : (
          /* INTERPRETER MODE - SIDE BY SIDE */
          <div className="flex flex-col h-full">
          <div className="grid grid-cols-2 gap-0 flex-1 overflow-hidden">
            {/* Left Column */}
            <div className={`${borderColor} border-r`}>
              <div className={`sticky top-0 ${darkMode ? 'bg-slate-950' : 'bg-white'} px-4 py-2 ${borderColor} border-b`}>
                <h3 className={`font-['Manrope'] text-sm font-semibold ${accentColor}`}>
                  {LANGUAGES.find((l) => l.code === selectedLang)?.name}
                </h3>
              </div>
              <div
                ref={scrollRef}
                className="overflow-y-auto px-3 py-3"
                style={{ maxHeight: 'calc(100vh - 160px)' }}
                data-testid="interpreter-subtitles-1"
              >
                {subtitles.length === 0 ? (
                  <p className={`font-['Inter'] text-sm ${textMuted} text-center py-10`}>{t.waitingSubtitles}</p>
                ) : (
                  <div className="space-y-3">
                    {subtitles.map((subtitle) => {
                      const isPlaying = subtitle.chunk_id && playingChunkIds.includes(subtitle.chunk_id);
                      return (
                      <div key={subtitle.id} data-chunk-id={subtitle.chunk_id || undefined} className={`${darkMode ? 'bg-blue-950/30 border-blue-900/50' : 'bg-blue-50 border-blue-200'} rounded-lg p-3 border transition-all duration-300 ${isPlaying ? (darkMode ? 'ring-1 ring-blue-500/50' : 'ring-1 ring-blue-400/50') : ''}`} data-testid="interpreter-subtitle-item-1">
                        {showOriginal && <p className={`font-['Inter'] text-xs ${textSecondary} mb-1`}>{subtitle.original}</p>}
                        <p className={`font-['Inter'] ${currentFontSize.text} ${textPrimary} font-medium`}>{subtitle.translated}</p>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className={`sticky top-0 ${darkMode ? 'bg-slate-950' : 'bg-white'} px-4 py-2 ${borderColor} border-b`}>
                <h3 className={`font-['Manrope'] text-sm font-semibold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                  {LANGUAGES.find((l) => l.code === secondLang)?.name}
                </h3>
              </div>
              <div
                ref={scrollRef2}
                className="overflow-y-auto px-3 py-3"
                style={{ maxHeight: 'calc(100vh - 160px)' }}
                data-testid="interpreter-subtitles-2"
              >
                {subtitles2.length === 0 ? (
                  <p className={`font-['Inter'] text-sm ${textMuted} text-center py-10`}>{t.waitingSubtitles}</p>
                ) : (
                  <div className="space-y-3">
                    {subtitles2.map((subtitle) => (
                      <div key={subtitle.id} className={`${darkMode ? 'bg-green-950/30 border-green-900/50' : 'bg-green-50 border-green-200'} rounded-lg p-3 border`} data-testid="interpreter-subtitle-item-2">
                        {showOriginal && <p className={`font-['Inter'] text-xs ${textSecondary} mb-1`}>{subtitle.original}</p>}
                        <p className={`font-['Inter'] ${currentFontSize.text} ${textPrimary} font-medium`}>{subtitle.translated}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Fixed interim for interpreter mode */}
          {(interimText || interimText2) && (
            <div className={`shrink-0 ${darkMode ? 'bg-slate-900/95 border-slate-700/50' : 'bg-white/95 border-slate-200'} border-t backdrop-blur-sm px-4 py-2`} data-testid="interpreter-interim-fixed">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  {interimText && (
                    <div className="flex items-center gap-1.5" data-testid="interpreter-interim-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                      <p className={`font-['Inter'] text-sm ${textSecondary} italic truncate`}>{interimText}</p>
                    </div>
                  )}
                </div>
                <div>
                  {interimText2 && (
                    <div className="flex items-center gap-1.5" data-testid="interpreter-interim-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                      <p className={`font-['Inter'] text-sm ${textSecondary} italic truncate`}>{interimText2}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          </div>
        )}
      </div>

      {/* Footer (hidden in monitor preview) */}
      {!monitorMode && (
      <div className={`${footerBg} border-t px-4 py-3`}>
        <div className="max-w-4xl mx-auto flex justify-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className={`text-xs ${btnGhost}`}
            data-testid="leave-session-btn"
          >
            {t.leaveSession}
          </Button>
        </div>
      </div>
      )}
    </div>
  );
}
